import {useEffect, useState} from 'react';
import {CallEvent, type MatrixCall} from 'matrix-js-sdk/lib/webrtc/call';
import {CallFeedEvent, type CallFeed} from 'matrix-js-sdk/lib/webrtc/callFeed';

export interface MatrixCallFeeds {
    readonly localUsermedia?: CallFeed;
    readonly localScreen?: CallFeed;
    readonly remoteUsermedia?: CallFeed;
    readonly remoteScreen?: CallFeed;
}

export interface MatrixCallMedia {
    readonly feeds: MatrixCallFeeds;
    readonly muted: boolean;
    readonly heldByMe: boolean;
    readonly heldByPeer: boolean;
    readonly dtmf: boolean;
}

const EMPTY: MatrixCallMedia = {feeds: {}, muted: false, heldByMe: false, heldByPeer: false, dtmf: false};

const readMedia = (call: MatrixCall | null): MatrixCallMedia =>
    call
        ? {
              feeds: {
                  localUsermedia: call.localUsermediaFeed,
                  localScreen: call.localScreensharingFeed,
                  remoteUsermedia: call.remoteUsermediaFeed,
                  remoteScreen: call.remoteScreensharingFeed,
              },
              muted: call.isMicrophoneMuted(),
              heldByMe: call.isRemoteOnHold(),
              heldByPeer: call.isLocalOnHold(),
              dtmf: call.opponentSupportsDTMF(),
          }
        : EMPTY;

export const hasLiveVideo = (feed: CallFeed | undefined): boolean =>
    !!feed && !feed.isVideoMuted() && feed.stream.getVideoTracks().some(track => track.readyState === 'live');

export const useMatrixCallMedia = (call: MatrixCall | null): MatrixCallMedia => {
    const [, setVersion] = useState(0);
    useEffect(() => {
        if (!call) {
            return;
        }
        const bump = () => setVersion(version => version + 1);
        let watched: CallFeed[] = [];
        const watchFeeds = () => {
            watched.forEach(feed => {
                feed.off(CallFeedEvent.MuteStateChanged, bump);
                feed.off(CallFeedEvent.NewStream, bump);
            });
            watched = [...call.getLocalFeeds(), ...call.getRemoteFeeds()];
            watched.forEach(feed => {
                feed.on(CallFeedEvent.MuteStateChanged, bump);
                feed.on(CallFeedEvent.NewStream, bump);
            });
            bump();
        };
        watchFeeds();
        call.on(CallEvent.FeedsChanged, watchFeeds);
        call.on(CallEvent.LocalHoldUnhold, bump);
        call.on(CallEvent.RemoteHoldUnhold, bump);
        call.on(CallEvent.State, bump);
        return () => {
            call.off(CallEvent.FeedsChanged, watchFeeds);
            call.off(CallEvent.LocalHoldUnhold, bump);
            call.off(CallEvent.RemoteHoldUnhold, bump);
            call.off(CallEvent.State, bump);
            watched.forEach(feed => {
                feed.off(CallFeedEvent.MuteStateChanged, bump);
                feed.off(CallFeedEvent.NewStream, bump);
            });
        };
    }, [call]);
    return readMedia(call);
};
